/**
 * Multi-add picker for the standard Items Catalog.
 *
 * Used in two places:
 *   • Room Defaults → Per-Room Defaults — adds picked names to the active
 *     room's default `items` list.
 *   • AddRoomDialog — adds picked names to the brand-new room being
 *     created (in addition to the room's defaults).
 *
 * Stays open after each pick (user can add several in a row), shows
 * picked-state checkmarks, and closes via the Done button or the X.
 *
 * Mirrors `EssentialPickerDialog` so users have one mental model.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Search, Plus, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  getFurnitureItemsCatalog,
  getFurnitureItemCategories,
  upsertFurnitureCatalogItem,
} from '@/lib/api';

export function ItemPickerDialog({
  open, onOpenChange, onPick,
  // Optional: names already present on the receiving list, so they show
  // as already-added without needing to add them again.
  existingNames = [],
  title = 'Add items',
}) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('existing');
  const [search, setSearch] = useState('');
  const [customName, setCustomName] = useState('');
  const [picking, setPicking] = useState(false);
  const [pickedSet, setPickedSet] = useState(new Set());

  // Names that block re-adding (existing on the receiving list + picked
  // this session). All compared lower-cased.
  const blockedSet = useMemo(() => {
    const s = new Set([...existingNames].map(n => (n || '').toLowerCase()));
    pickedSet.forEach(n => s.add(n));
    return s;
  }, [existingNames, pickedSet]);

  useEffect(() => {
    if (!open) return;
    setSearch(''); setCustomName(''); setTab('existing'); setPickedSet(new Set());
    setLoading(true);
    Promise.all([getFurnitureItemsCatalog(), getFurnitureItemCategories()])
      .then(([is, cs]) => { setItems(is); setCategories(cs); })
      .catch(() => toast.error('Could not load items catalog'))
      .finally(() => setLoading(false));
  }, [open]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const visible = q ? items.filter(i => i.name.toLowerCase().includes(q)) : items;
    const known = new Map();
    categories.forEach(c => known.set(c.name, []));
    const orphans = new Map();
    const uncat = [];
    const fromProjects = [];
    for (const it of visible) {
      const cat = it.category;
      if (!cat) {
        if (it.from_project) fromProjects.push(it);
        else uncat.push(it);
        continue;
      }
      if (known.has(cat)) known.get(cat).push(it);
      else {
        if (!orphans.has(cat)) orphans.set(cat, []);
        orphans.get(cat).push(it);
      }
    }
    const out = categories
      .map(c => ({ key: c.id, name: c.name, items: known.get(c.name) || [] }))
      .filter(g => g.items.length > 0);
    [...orphans.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([name, items]) => {
      out.push({ key: `orphan-${name}`, name, items });
    });
    if (uncat.length) out.push({ key: 'uncat', name: 'Uncategorized', items: uncat });
    if (fromProjects.length) out.push({ key: 'from-projects', name: 'From Past Projects', items: fromProjects });
    for (const g of out) g.items.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [items, categories, search]);

  const handlePick = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setPicking(true);
    try {
      await onPick(trimmed);
      // For custom-tab adds, also save back into the catalog so the entry
      // shows up next time someone opens the picker.
      if (tab === 'custom' && !items.some(i => i.name.toLowerCase() === trimmed.toLowerCase())) {
        try {
          const created = await upsertFurnitureCatalogItem(trimmed, { category: null });
          setItems(prev => [...prev, created]);
        } catch { /* non-fatal */ }
      }
      setPickedSet(prev => {
        const next = new Set(prev);
        next.add(trimmed.toLowerCase());
        return next;
      });
      setCustomName('');
      toast.success(`Added: ${trimmed}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add');
    } finally {
      setPicking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="item-picker-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Pick from your items catalog or add a custom one. The dialog stays open so you can add several in a row — close it when you're done.
            {pickedSet.size > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 text-emerald-700 font-medium">
                · {pickedSet.size} added this session
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="self-start">
            <TabsTrigger value="existing" data-testid="item-picker-tab-existing">Choose existing</TabsTrigger>
            <TabsTrigger value="custom" data-testid="item-picker-tab-custom">Custom</TabsTrigger>
          </TabsList>

          <TabsContent value="existing" className="flex-1 flex flex-col min-h-0 mt-3">
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9ca3af]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search items or categories…"
                className="pl-8 h-9 text-sm"
                autoFocus
                data-testid="item-picker-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto pr-1 -mr-1">
              {loading ? (
                <div className="py-8 text-center text-sm text-[#9ca3af]">
                  <Loader2 className="h-4 w-4 inline animate-spin mr-1" /> Loading…
                </div>
              ) : grouped.length === 0 ? (
                <div className="py-8 text-center text-sm text-[#9ca3af]">
                  {search ? 'No matches.' : 'Items catalog is empty. Use the Custom tab to add your first one.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {grouped.map(group => (
                    <div key={group.key}>
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-[#5b6773] px-1 mb-1">
                        {group.name} <span className="text-[#9ca3af]">({group.items.length})</span>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {group.items.map(entry => {
                          const wasPicked = pickedSet.has(entry.name.toLowerCase());
                          const alreadyOnList = !wasPicked && blockedSet.has(entry.name.toLowerCase());
                          const disabled = picking || wasPicked || alreadyOnList;
                          return (
                            <button
                              key={entry.name_key}
                              type="button"
                              disabled={disabled}
                              onClick={() => handlePick(entry.name)}
                              className={`text-left flex items-center gap-2 px-2 py-1.5 rounded disabled:cursor-not-allowed group ${wasPicked ? 'bg-emerald-50' : alreadyOnList ? 'opacity-60' : 'hover:bg-[#2d948c]/10'}`}
                              data-testid={`item-picker-item-${entry.name_key.replace(/\s+/g, '-')}`}
                            >
                              <span className={`text-[13px] flex-1 truncate ${wasPicked ? 'text-emerald-800' : 'text-[#1f2933]'}`}>
                                {entry.name}
                              </span>
                              {alreadyOnList && (
                                <span className="text-[10px] text-[#9ca3af] italic">already added</span>
                              )}
                              {wasPicked ? (
                                <Check className="h-3.5 w-3.5 text-emerald-700 flex-shrink-0" />
                              ) : !alreadyOnList ? (
                                <Plus className="h-3.5 w-3.5 text-[#2d948c] opacity-0 group-hover:opacity-100" />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="custom" className="mt-3">
            <div className="space-y-2">
              <p className="text-xs text-[#5b6773]">
                Add a one-off item by name. It will be saved to the catalog so you can reuse it later.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePick(customName); } }}
                  placeholder="e.g. Velvet Accent Chair"
                  autoFocus
                  data-testid="item-picker-custom-name"
                />
                <Button onClick={() => handlePick(customName)} disabled={!customName.trim() || picking} className="bg-emerald-700 hover:bg-emerald-800" data-testid="item-picker-custom-add">
                  Add
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="item-picker-done">
            {pickedSet.size > 0 ? `Done (${pickedSet.size} added)` : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
